import React, { createContext, useContext, useState, ReactNode } from 'react';

interface UpgradeContextType {
  isUpgradeModalOpen: boolean;
  returnPath: string | null;
  limitType: 'staff' | 'projects' | 'billExtractions' | 'integrations' | null;
  limitDetails: {
    max?: number;
    current?: number;
    remaining?: number;
  } | null;
  openUpgradeModal: (options: {
    returnPath?: string;
    limitType?: 'staff' | 'projects' | 'billExtractions' | 'integrations';
    limitDetails?: { max?: number; current?: number; remaining?: number };
  }) => void;
  closeUpgradeModal: () => void;
  completeUpgrade: () => void;
}

const UpgradeContext = createContext<UpgradeContextType | undefined>(undefined);

export const useUpgrade = () => {
  const context = useContext(UpgradeContext);
  if (!context) {
    throw new Error('useUpgrade must be used within an UpgradeProvider');
  }
  return context;
};

export const UpgradeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [returnPath, setReturnPath] = useState<string | null>(null);
  const [limitType, setLimitType] = useState<'staff' | 'projects' | 'billExtractions' | 'integrations' | null>(null);
  const [limitDetails, setLimitDetails] = useState<{ max?: number; current?: number; remaining?: number } | null>(null);

  const openUpgradeModal = (options: {
    returnPath?: string;
    limitType?: 'staff' | 'projects' | 'billExtractions' | 'integrations';
    limitDetails?: { max?: number; current?: number; remaining?: number };
  }) => {
    setReturnPath(options.returnPath || window.location.pathname);
    setLimitType(options.limitType || null);
    setLimitDetails(options.limitDetails || null);
    setIsUpgradeModalOpen(true);
  };

  const closeUpgradeModal = () => {
    setIsUpgradeModalOpen(false);
    // Don't clear returnPath immediately - keep it for potential retry
  };

  const completeUpgrade = () => {
    setIsUpgradeModalOpen(false);
    // Return path navigation will be handled by the modal component
    // after a brief delay to show success state
  };

  return (
    <UpgradeContext.Provider
      value={{
        isUpgradeModalOpen,
        returnPath,
        limitType,
        limitDetails,
        openUpgradeModal,
        closeUpgradeModal,
        completeUpgrade,
      }}
    >
      {children}
    </UpgradeContext.Provider>
  );
};

